import React, { useCallback, useEffect, useState } from "react";
import { Flame, RefreshCw, MoonStar, XCircle, MapPin, Users } from "lucide-react";
import { hotspotsApi, apiErrorMessage } from "@/api/client";
import { EmptyState } from "@/components/EmptyState";
import { ChannelBadge } from "@/components/ChannelBadge";
import { toast } from "sonner";

const TYPE_META = {
  PROSPECTING_FHRS: { cls: "bg-orange-50 text-orange-700 border-orange-200", label: "FHRS cluster" },
  DEMAND: { cls: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", label: "Demand" },
  MIXED: { cls: "bg-teal-50 text-teal-700 border-teal-200", label: "Mixed" },
};

const DIMENSIONS = [
  { key: "caterctrl_fit_strength", label: "CaterCTRL fit", bar: "bg-orange-500" },
  { key: "multi_product_value", label: "Multi-product", bar: "bg-teal-600" },
  { key: "geographic_compactness", label: "Compactness", bar: "bg-sky-600" },
  { key: "field_day_suitability", label: "Field-day fit", bar: "bg-violet-600" },
  { key: "relationship_strength", label: "Relationship", bar: "bg-fuchsia-600" },
];

function DimensionBars({ h }) {
  const dims = DIMENSIONS.filter((d) => typeof h[d.key] === "number");
  if (dims.length === 0) return null;
  return (
    <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3" data-testid={`hotspot-dimensions-${h.id}`}>
      {dims.map((d) => (
        <div key={d.key} data-testid={`hotspot-dim-${d.key}-${h.id}`}>
          <div className="flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            <span>{d.label}</span>
            <span className="text-slate-600" data-testid={`hotspot-dim-${d.key}-value-${h.id}`}>{h[d.key]}</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <div className={`h-full rounded-full ${d.bar}`} style={{ width: `${Math.max(0, Math.min(100, h[d.key]))}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function HotspotProspects({ id }) {
  const [state, setState] = useState("idle"); // idle | loading | loaded
  const [items, setItems] = useState([]);
  const toggle = async () => {
    if (state === "loaded") { setState("idle"); return; }
    setState("loading");
    try { const { data } = await hotspotsApi.prospects(id); setItems(data.items); setState("loaded"); }
    catch (e) { toast.error(apiErrorMessage(e)); setState("idle"); }
  };
  return (
    <div className="mt-3 border-t border-slate-100 pt-2">
      <button data-testid={`hotspot-prospects-toggle-${id}`} onClick={toggle}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700">
        <Users className="h-3 w-3" /> {state === "loaded" ? "Hide" : "Show"} prospects{state === "loading" ? "…" : ""}
      </button>
      {state === "loaded" && (
        items.length === 0 ? <p className="mt-1 text-[11px] text-slate-400">No prospect organisations in this cluster yet.</p> :
        <ul className="mt-2 space-y-1.5" data-testid={`hotspot-prospects-${id}`}>
          {items.map((p) => (
            <li key={p.organisation_id} data-testid={`hotspot-prospect-${p.organisation_id}`}
              className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-1.5">
              <span className="min-w-0">
                <span className="block truncate text-xs font-semibold text-slate-700">{p.name}</span>
                <span className="text-[10px] text-slate-400">{p.best_fit || "—"}{p.fhrs_rating ? ` · FHRS ${p.fhrs_rating}` : ""}</span>
              </span>
              <ChannelBadge channel={p.channel} reasons={p.reasons} testid={`hotspot-channel-${p.organisation_id}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Hotspots() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await hotspotsApi.list(); setItems(data.items); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    setBusy(true);
    try { const { data } = await hotspotsApi.recompute(); toast.success(`${data.created} new · ${data.detected} detected`); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(apiErrorMessage(e)); } };

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 md:py-8" data-testid="hotspots-page">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Hotspots</h1>
          <p className="mt-0.5 text-xs text-slate-400">Deterministic demand & cold-prospect clusters. Each explains why it exists.</p>
        </div>
        <button data-testid="hotspots-recompute" onClick={recompute} disabled={busy}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> Recompute
        </button>
      </div>

      {loading ? <div className="h-32 animate-pulse rounded-xl border border-slate-200 bg-white" /> :
        items.length === 0 ? <EmptyState testid="hotspots-empty" icon={Flame} title="No hotspots yet" description="Recompute once you have discovered prospects or website intent." /> :
        <div className="space-y-2.5" data-testid="hotspots-list">
          {items.map((h) => {
            const m = TYPE_META[h.type] || TYPE_META.MIXED;
            return (
              <div key={h.id} data-testid={`hotspot-${h.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Flame className="h-4 w-4 text-orange-500" />
                    <span className={`rounded-md border px-2 py-0.5 text-xs font-bold ${m.cls}`}>{m.label}</span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500">{h.status}</span>
                  </div>
                  <span className="text-[11px] text-slate-400"><MapPin className="mr-0.5 inline h-3 w-3" />{h.centre?.lat?.toFixed(3)}, {h.centre?.lng?.toFixed(3)}</span>
                </div>
                <ul className="mt-2 space-y-0.5" data-testid={`hotspot-evidence-${h.id}`}>
                  {(h.evidence || []).map((e, i) => (
                    <li key={i} className="flex items-start gap-1 text-[11px] text-slate-500"><span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-orange-400" /> {e}</li>
                  ))}
                </ul>
                <DimensionBars h={h} />
                <HotspotProspects id={h.id} />
                <div className="mt-3 flex justify-end gap-2">
                  <button data-testid={`hotspot-snooze-${h.id}`} onClick={() => act(() => hotspotsApi.snooze(h.id, new Date(Date.now() + 7 * 864e5).toISOString()), "Snoozed")}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-50"><MoonStar className="h-3 w-3" /> Snooze</button>
                  <button data-testid={`hotspot-dismiss-${h.id}`} onClick={() => act(() => hotspotsApi.dismiss(h.id), "Dismissed")}
                    className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2 py-1 text-[11px] font-semibold text-rose-600 hover:bg-rose-50"><XCircle className="h-3 w-3" /> Dismiss</button>
                </div>
              </div>
            );
          })}
        </div>}
    </div>
  );
}
